import * as Sentry from "@sentry/react";
import { generateEd25519Key, generateSecp256k1Key } from "./generate";
import {
  ed25519PrivateKeyToExportBase58,
  evmAddressFromSecp256k1,
  privateKeyToExportHex,
  solanaAddressFromEd25519,
  tronAddressFromSecp256k1,
} from "./addresses";
import {
  deleteWallet as deleteFromStorage,
  loadMeta,
  loadPrivateKeys,
  markBackedUp,
  saveWallet,
} from "./storage";
import type { ChainKey, GlideWallet } from "./types";

export type { ChainKey, GlideWallet };

// Single, eager local wallet initialization shared by every consumer. Kicking
// this off once (and awaiting the same promise everywhere) guarantees session
// creation can wait for the deposit owner address instead of racing an
// unfinished init and silently dropping depositOwnerAddress.
let localWalletPromise: Promise<GlideWallet | null> | null = null;

export function ensureLocalWallet(): Promise<GlideWallet | null> {
  if (!localWalletPromise) {
    localWalletPromise = (async () => {
      try {
        return getWallet() ?? (await createWallet());
      } catch (e) {
        Sentry.captureException(e, {
          tags: { component: "GlideWallet", action: "init" },
        });
        // Clear the memoized promise so a later caller (e.g. the next session
        // creation) retries after a transient IndexedDB/storage failure instead
        // of being stuck with a permanent null for the page lifetime.
        localWalletPromise = null;
        return null;
      }
    })();
  }
  return localWalletPromise;
}

/**
 * Maps a CAIP-2 chain ID to the GlideWallet chain key for that ecosystem.
 * Uses the CAIP-2 namespace (the part before ":") for the mapping.
 */
export function chainKeyFromCAIP2(caip2: string): ChainKey | undefined {
  const namespace = caip2.split(":")[0];
  switch (namespace) {
    case "eip155":
      return "evm";
    case "solana":
      return "solana";
    case "tron":
      return "tron";
    default:
      return undefined;
  }
}

/**
 * Returns the wallet address for the chain ecosystem of the given CAIP-2 chain ID.
 */
export function getAddressForChainId(
  wallet: GlideWallet,
  chainId: string,
): string | undefined {
  const chainKey = chainKeyFromCAIP2(chainId);
  return chainKey ? getAddress(wallet, chainKey) : undefined;
}

export async function createWallet(): Promise<GlideWallet> {
  const secp256k1Key = generateSecp256k1Key();
  const ed25519Key = generateEd25519Key();

  const evmAddress = evmAddressFromSecp256k1(secp256k1Key);
  const solanaAddress = solanaAddressFromEd25519(ed25519Key);
  const tronAddress = tronAddressFromSecp256k1(secp256k1Key);

  const meta = {
    createdAt: Date.now(),
    evmAddress,
    solanaAddress,
    tronAddress,
    backedUp: false,
  };

  try {
    await saveWallet(secp256k1Key, ed25519Key, meta);
    return { evmAddress, solanaAddress, tronAddress, meta };
  } finally {
    secp256k1Key.fill(0);
    ed25519Key.fill(0);
  }
}

export function getWallet(): GlideWallet | null {
  const meta = loadMeta();
  if (!meta) return null;

  return {
    evmAddress: meta.evmAddress,
    solanaAddress: meta.solanaAddress,
    tronAddress: meta.tronAddress,
    meta,
  };
}

export async function exportPrivateKey(
  chain: ChainKey,
): Promise<string | null> {
  const keys = await loadPrivateKeys();
  if (!keys) return null;

  const { secp256k1Key, ed25519Key } = keys;

  try {
    let result: string;
    switch (chain) {
      case "evm":
        result = privateKeyToExportHex(secp256k1Key);
        break;
      case "tron":
        result = privateKeyToExportHex(secp256k1Key);
        break;
      case "solana":
        result = ed25519PrivateKeyToExportBase58(ed25519Key);
        break;
    }
    return result;
  } finally {
    secp256k1Key.fill(0);
    ed25519Key.fill(0);
  }
}

export function confirmBackedUp(): void {
  markBackedUp();
}

export async function deleteWallet(): Promise<void> {
  await deleteFromStorage();
}

export function getAddress(wallet: GlideWallet, chain: ChainKey): string {
  switch (chain) {
    case "evm":
      return wallet.evmAddress;
    case "solana":
      return wallet.solanaAddress;
    case "tron":
      return wallet.tronAddress;
  }
}
